import React from 'react';

import CustomIcon from '../Common/CustomIcon/CustomIcon';

import {
	generateChecksum,
} from 'Vulture/Checksum';

import {
	importAccounts,
	MAX_ACCOUNT_IMPORT_ROWS,
} from 'Vulture/Account';

// Styles are shared with the Listings import modal on purpose — DEV-11951 asks for the same
// look and behavior as the Listings CSV upload.
import style from './Listings.module.css';

/**
 * Bulk account import (DEV-11951). Internal admin only; gated at the button in Accounts.js
 * and enforced again server-side.
 *
 * Two views live in this one modal so there is no nested-modal stacking:
 *  - 'pick'   — choose a CSV, preview the first four lines, Save
 *  - 'report' — what got created and what got eliminated, and why
 */
class AccountsUploadModal extends React.Component {
	constructor(props) {
		super(props);

		this.uploadCSVRef = React.createRef();

		this.state = {
			CSVfile:           null,
			fileName:          '',
			lastModified:      '',
			lineCount:         0,
			nonBlankLineCount: 0,
			firstFourLines:    [],
			report:            null,
		};
	}

	// Data rows = non-blank lines minus the header. Blank lines have to be excluded or an
	// interior empty row makes a valid 50-account file look like 51 and blocks Save on a file
	// the server would happily accept.
	dataRowCount() {
		return Math.max(0, this.state.nonBlankLineCount - 1);
	}

	tooManyRows() {
		return this.dataRowCount() > MAX_ACCOUNT_IMPORT_ROWS;
	}

	resetFile() {
		this.setState({
			CSVfile:           null,
			fileName:          '',
			lineCount:         0,
			nonBlankLineCount: 0,
			firstFourLines:    [],
		});
		if (this.uploadCSVRef.current) {
			this.uploadCSVRef.current.value = '';
		}
	}

	setCSVFile(file) {
		if (!file) {
			return;
		}

		const reader = new FileReader();
		reader.onload = (evt) => {
			const content = evt.target.result;
			const contentWithoutQuotes = content.replace(/"/g, ''); // Remove all quotation marks
			const lines = contentWithoutQuotes.split('\n');
			const lineCount = lines.length - (lines[lines.length - 1] === '' ? 1 : 0); // Subtract 1 if the last line is empty
			const nonBlankLineCount = lines.filter((line) => {
				return line.replace(/,/g, '').trim() !== '';
			}).length;
			const firstFourLines = lines.slice(0, 4);

			this.setState({
				CSVfile:      file,
				fileName:     file.name,
				lastModified: (
					file.lastModifiedDate ||
					file.lastModified ||
					Date.now()
				).toString(),
				lineCount,
				nonBlankLineCount,
				firstFourLines,
			});
		};
		reader.readAsText(file);
	}

	async uploadCSV() {
		const {
			app,
		} = this.props;

		const {
			CSVfile,
		} = this.state;

		if (!CSVfile || this.tooManyRows()) {
			return;
		}

		let checksum;
		try {
			checksum = await generateChecksum(CSVfile);
		} catch (err) {
			alert(`Failed doing the checksum: ${err}`);
			this.resetFile();
			return;
		}

		app.load(async () => {
			const data = await importAccounts(CSVfile, checksum);

			if (data?.err) {
				alert(`Failed to import accounts: ${data.err}`);
				this.resetFile();
			} else {
				this.setState({
					report: {
						created: data?.created || [],
						skipped: data?.skipped || [],
					},
				});
			}

			app.setState({
				loading: false,
			});
		});
	}

	// The accounts table is driven by /account/search, not by the account list, so re-running
	// the active search is what actually surfaces the imported accounts. Deliberately NOT
	// app.apiFetchUserAccounts(): that reads user_to_account_map, which an import doesn't
	// write to, so it would reset the user's filters without showing anything new.
	done() {
		const {
			app,
			onImported,
		} = this.props;

		const created = this.state.report?.created || [];

		app.closeModal();

		if (created.length > 0 && typeof onImported === 'function') {
			onImported(created);
		}
	}

	renderReport() {
		const {
			created,
			skipped,
		} = this.state.report;

		return (
			<div className={style.listingsModal} data-test-id="accountsImportReport">
				<div className={style.listingsModalContainer}>
					<div className={style.column1}>
						<p data-test-id="accountsImportCreatedCount">
							<span className={style.infoTitle}>Accounts created: </span>{created.length}
						</p>
						<p data-test-id="accountsImportSkippedCount">
							<span className={style.infoTitle}>Rows eliminated: </span>{skipped.length}
						</p>
						{skipped.length > 0 &&
							<div className={style.tableContainer} data-test-id="accountsImportSkippedTable">
								<div className={style.tableRow}>
									<b className={style.tableItem}><span>Row</span></b>
									<b className={style.tableItem}><span>Account Name</span></b>
									<b className={style.tableItem}><span>Salesforce ID</span></b>
									<b className={style.tableItem}><span>Reason</span></b>
								</div>
								{skipped.map((row, i) => {
									return (
										<div className={style.tableRow} key={i} data-test-id="accountsImportSkippedRow">
											<span className={style.tableItem}>{row.row}</span>
											<span className={style.tableItem}>{row.display_name}</span>
											<span className={style.tableItem}>{row.salesforce_id_val}</span>
											<span className={style.tableItem}>{row.reason}</span>
										</div>
									);
								})}
							</div>
						}
					</div>
				</div>
				<div className={style.bottom}>
					<button
						onClick={(e) => {
							e.preventDefault();
							this.done();
						}}
						className="button"
						data-test-id="accountsImportDoneButton"
					>
						Done
					</button>
				</div>
			</div>
		);
	}

	render() {
		const {
			app,
		} = this.props;

		const {
			CSVfile,
			fileName,
			lastModified,
			lineCount,
			firstFourLines,
			report,
		} = this.state;

		if (report) {
			return this.renderReport();
		}

		const tooManyRows = this.tooManyRows();
		const saveDisabled = !CSVfile || tooManyRows;

		return (
			<>
				<div className={style.listingsModal} data-test-id="accountsImportModal">
					<div className={style.listingsModalContainer}>
						<div className={style.column1}>
							<button
								onClick={(e) => {
									e.preventDefault();
									this.uploadCSVRef.current.click();
								}}
								className="button"
								data-test-id="accountsImportUploadButton"
							>
								Upload
							</button>
							<p><span className={style.infoTitle}>Filename: </span>{`${fileName}`}</p>
							<p><span className={style.infoTitle}>Lines: </span>{`${lineCount}`}</p>
							<p><span className={style.infoTitle}>Last Modified: </span>{`${lastModified}`}</p>
							{tooManyRows &&
								<p data-test-id="accountsImportTooManyRows">
									{`This file has ${this.dataRowCount()} accounts. The maximum is ${MAX_ACCOUNT_IMPORT_ROWS} at a time.`}
								</p>
							}
							<div className={style.tableContainer}>
								{firstFourLines.map((line, i) => {
									if (i === 0) {
										return (<div className={style.tableRow} key={i}>
											{line.split(',').map((piece, j) => {
												return (
													<b className={style.tableItem} key={`${i}_${j}`}>
														<span>
															{piece}
														</span>
													</b>
												);
											})}
										</div>);
									}
									return (<div className={style.tableRow} key={i}>
										{line.split(',').map((piece, j) => {
											return (
												<span className={style.tableItem} key={`${i}_${j}`}>
													{piece}
												</span>
											);
										})}
									</div>);
								})}
							</div>
						</div>
					</div>
					<div className={style.bottom}>
						<button
							onClick={(e) => {
								e.preventDefault();
								app.closeModal();
							}}
							className="button-outlined"
							data-test-id="accountsImportCancelButton"
						>
							Cancel
						</button>
						<button
							onClick={(e) => {
								e.preventDefault();
								this.uploadCSV();
							}}
							className={`button ${saveDisabled ? 'button-disabled no-click' : ''}`}
							data-test-id="accountsImportSaveButton"
						>
							<CustomIcon icon='save' /> Save
						</button>
					</div>
				</div>
				<input
					data-test-id="accountsImportFileInput"
					onChange={(e) => {
						this.setCSVFile(e.target.files[0]);
					}}
					accept='.csv'
					ref={this.uploadCSVRef}
					type='file'
					style={{
						display: 'none',
					}}
				/>
			</>
		);
	}
}

export default AccountsUploadModal;
